import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { unawaited } from '../../utils/promise';
import { fromAuth, useAppDispatch } from '../../store';

export function useRefreshWithRetries() {
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const { events, user } = auth;
  const accessToken = user?.access_token ?? '';
  const refreshToken = user?.refresh_token ?? '';

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [error, setError] = useState<Error | null>(null);

  const refreshWithRetries = async () => {
    const accessTokenPayload = jwtDecode(accessToken);
    const refreshTokenPayload = jwtDecode(refreshToken);
    const accessTokenExpiresAt = accessTokenPayload.exp ?? 0;
    const refreshTokenExpiresAt = refreshTokenPayload.exp ?? 0;
    if (refreshTokenExpiresAt <= accessTokenExpiresAt) {
      throw new Error('Refresh token has the same TTL as access token');
    }

    setStatus('pending');
    const signedIn = await auth.signinSilent();
    if (signedIn === null) {
      setStatus('error');
      setError(new Error('Could not sign in silently'));
    } else {
      setStatus('success');
      await dispatch(fromAuth.doPostSignIn({ jwt: signedIn.access_token }));
      setError(null);
    }
  };

  useEffect(() => {
    const cb = () => refreshWithRetries();

    events.addAccessTokenExpired(cb);
    events.addAccessTokenExpiring(cb);
    return () => {
      events.removeAccessTokenExpired(cb);
      events.removeAccessTokenExpiring(cb);
    };
  }, [events, refreshWithRetries]);

  const isValid = useMemo(() => {
    if (accessToken.length === 0) {
      return false;
    }

    const payload = jwtDecode(accessToken);
    const expiresAt = payload?.exp ?? 0;
    const isValid = expiresAt * 1000 >= Date.now();

    return isValid;
  }, [accessToken]);

  useEffect(() => {
    if (status === 'error' && !isValid) {
      // expired token with failure refresh attempts -> let's just remove user to force a sign out
      // it's likely that the KeyCloak session has become inactive already
      unawaited(auth.removeUser());
    }
  }, [auth, isValid, status]);

  return { error, status };
}
